import { context } from '@shared-services/context-service';
import { logger } from '@shared-services/log-service';
import { isObjectEmpty } from 'client/utils';
import { UploadResult } from 'client/widget-components/components/FileUpload/types';
import { PRODUCT_ONE_TIME_PAYMENT_PLAN_ID } from 'client/widget-components/ecom/consts';
import CustomizationsState from 'client/widget-components/ecom/stores/CustomizationsState';
import {
    DEFAULT_VARIATION_ID,
    ProductState,
} from 'client/widget-components/ecom/stores/ProductState';
import {
    Customization,
    ProductEntity,
    ProductPaymentPlan,
} from 'client/widget-components/ecom/stores/types';
import {
    CustomizationSnipcartPayloadFile,
    CustomizationType,
    ProductCustomization,
} from 'client/widget-components/ecom/types/EcomSDK';
import { isProductCustomizationsEnabled } from 'client/widgets/runtime-product-customizations/utils';
import { MouseEvent } from 'react';

export type ComponentProps = {
    isDisabled: boolean;
    isSubscribe: boolean;
};

export const useAddToCart = ({
    product,
    productState,
}: {
    product: ProductEntity;
    productState: ProductState;
}) => {
    if (isObjectEmpty(product)) {
        return { elementAttributes: {}, componentProps: {} };
    }
    const { itemId, productId, price, internal_quantity } = product;

    const { customizations } = productState;

    const quantityEnabled = !!internal_quantity || internal_quantity === 0;
    const shouldDisable = quantityEnabled && internal_quantity! <= 0;
    const isSubscribe = getIsSubscribe(productState.selectedPaymentPlan);

    const onClick = async (e: MouseEvent<HTMLElement>) => {
        if (
            context.isEditor ||
            !window.dmAPI ||
            !window._ecomm ||
            shouldDisable
        ) {
            return;
        }

        const isCustomizationsEnabled = isProductCustomizationsEnabled();

        e.stopPropagation();

        customizations.validateAll();
        if (!customizations.isValid() && isCustomizationsEnabled) {
            return;
        }

        try {
            const payload = {
                product_id: productId,
                variation_id:
                    productState.selectedVariation?.identifier ||
                    DEFAULT_VARIATION_ID,
                quantity: 1,
                product_customizations: isCustomizationsEnabled
                    ? getAddProductCustomizationsPayload(customizations)
                    : [],
            };

            await window._ecomm.ready;

            if (isSubscribe) {
                await window._ecomm?.buyProduct?.({
                    ...payload,
                    plan_id: productState.selectedPaymentPlan?.id || '',
                });
            } else {
                await window._ecomm?.addProduct?.(payload);
            }
        } catch (error) {
            const objectToLog = {
                message: `Error in window._ecomm?.${
                    isSubscribe ? 'buyProduct' : 'addProduct'
                }`,
                tags: [
                    '__new-runtime__',
                    'native-ecom',
                    isSubscribe ? 'subscribe-to-product' : 'add-items-to-cart',
                ],
                errorMessage: error,
            };
            logger.error(objectToLog);
        }
    };

    const elementAttributes = {
        'data-item-id': itemId,
        'data-item-price': price,
        'data-auto': 'addToCart',
        onClick,
    };

    const componentProps: ComponentProps = {
        isDisabled: shouldDisable,
        isSubscribe,
    };

    return { elementAttributes, componentProps };
};

const getIsSubscribe = (paymentPlan: ProductPaymentPlan | null) =>
    !!paymentPlan && paymentPlan.id != PRODUCT_ONE_TIME_PAYMENT_PLAN_ID;

const formatFileCustomizationPayload = (
    customization: Customization,
    value: UploadResult[] = []
) => ({
    id: customization.id,
    type: customization.type,
    value: value.map(({ uploadedAt, ...file }) => ({
        ...file,
        uploaded_at: uploadedAt,
    })) as CustomizationSnipcartPayloadFile[],
});

const getAddProductCustomizationsPayload = (
    customizations: CustomizationsState
) =>
    customizations.list.map((customization) => {
        const value = customizations.values[customization.id];

        if (customization.type === CustomizationType.FILE) {
            return formatFileCustomizationPayload(
                customization,
                value as UploadResult[]
            );
        }

        return {
            id: customization.id,
            type: customization.type,
            value: customizations.values[customization.id],
        } as ProductCustomization;
    });
